import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import FormConstants from '@shared/constants/FormConstants';
import {
  editPurgeAndLegalHold,
  getSearchForm
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {useSnackbar} from 'notistack';
import {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  BASE_MENU_ITEM,
  BASE_ROUTE,
  BASE_ROUTE_MODULE
} from 'src/routes/BaseRoute';
import TabsComponent from '../../sub-modules-menu/tabsComponent/TabsComponent';
import ModuleSearchFilter from '../module-search-filter/ModuleSearchFilter';
import EncryptUtils from 'src/utils/EncryptUtils';
import ActionModalDialog from '@components/dialog/action-dialog/ActionModalDialog';
import {APP} from 'src/styles/variables';
import {useAppContext} from '@context/AppContext';
import {LoadingScreen} from 'src/minimal-components/loading-screen';
import CustomBreadcrumbs from 'src/minimal-components/custom-breadcrumbs/custom-breadcrumbs';

interface PurgeLegalHoldComponentProps {
  module: string;
}
interface StateProps {
  tabData: any;
  loader: boolean;
  selectedAddNewTab: string;
  cancelData: boolean;
  openConfirmationBox: boolean;
  rowId: any;
  dialogButtonLoader: boolean;
  apiLoader: boolean;
}
const PurgeAndLegalHoldComponent = (
  props: PurgeLegalHoldComponentProps
): React.ReactElement => {
  const {
    appState: {applicationName}
  } = useAppContext();

  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();
  const locationState = useLocation();
  const [state, setState] = useState<StateProps>({
    tabData: {},
    loader: false,
    selectedAddNewTab: '',
    cancelData: false,
    openConfirmationBox: false,
    rowId: '',
    dialogButtonLoader: false,
    apiLoader: false
  });

  const [dialogInfo, setDialogInfo] = useState<any>({
    dialogTitle: '',
    dialogDescription: '',
    yesLabelText: '',
    noLabelText: '',
    yesButtonColor: '',
    noButtonColor: ''
  });

  const navigate = useNavigate();
  const {
    tabData,
    loader,
    selectedAddNewTab,
    openConfirmationBox,
    dialogButtonLoader,
    apiLoader
  } = state;

  const init = async () => {
    try {
      if (props.module) {
        setState((prevState: StateProps) => ({
          ...prevState,
          loader: true
        }));
        const res = await getSearchForm(applicationName, props.module);
        setState((prevState: StateProps) => ({
          ...prevState,
          tabData: res.searchForm,
          loader: false
        }));
        const searchParam =
          locationState.search.split('?')?.[1]?.split('=')?.[1] ||
          locationState.search.split('?')?.[1]?.split('=')?.[0];
        navigate(
          `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${
            searchParam === FormConstants.MENU_ITEM.LEGAL_HOLD
              ? FormConstants.PURGE_TERMS.LEGAL_HOLD
              : searchParam === FormConstants.MENU_ITEM.PURGE
              ? FormConstants.PURGE_TERMS.PURGE
              : searchParam || FormConstants.PURGE_TERMS.PURGE
          }`,
          {
            state: {}
          }
        );
      }
    } catch (e) {
      const errMessage = getErrorMessage(e);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());
      setState(prevState => ({
        ...prevState,
        loader: false
      }));
    }
  };
  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAddNewRecord = (data: any) => {
    setState(prevState => ({
      ...prevState,
      selectedAddNewTab: data || FormConstants.PURGE_TERMS.PURGE
    }));
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${
        data || FormConstants.PURGE_TERMS.PURGE
      }/${FormConstants.PURGE_TERMS.ADD_NEW}`,
      {
        state: {}
      }
    );
  };
  const handleCancelButton = (route: string) => {
    setState(prevState => ({
      ...prevState,
      selectedAddNewTab: ''
    }));
    navigate(
      `/${BASE_ROUTE}/${BASE_ROUTE_MODULE}/${props?.module}?${BASE_MENU_ITEM}=${route}`,
      {
        state: {}
      }
    );
  };

  const handleButtonSubmit = () => {
    // handleCancelButton()
  };

  const deleteLegalHold = async (data: any) => {
    const payload: any = {};

    payload.row_id = state.rowId;
    payload.action = FormConstants.PURGE_TERMS.LEGAL_HOLD_DELETE;
    payload.sql_data = tabData?.sub_sections?.[1]?.update_is_enable_sql;

    try {
      setState((prevState: StateProps) => ({
        ...prevState,
        dialogButtonLoader: true,
        apiLoader: true
      }));

      const encryptRequestData = EncryptUtils.encryptedData(payload);
      const res = await editPurgeAndLegalHold({tk: encryptRequestData});

      setState((prevState: StateProps) => ({
        ...prevState,
        dialogButtonLoader: false,
        openConfirmationBox: false,
        apiLoader: false
      }));

      enqueueSnackbar(res?.message, SnackBarConfig.getSuccess());
    } catch (error) {
      const errMessage = getErrorMessage(error);
      enqueueSnackbar(errMessage, SnackBarConfig.getError());

      setState((prevState: StateProps) => ({
        ...prevState,
        dialogButtonLoader: false,
        openConfirmationBox: false,
        apiLoader: false
      }));
    }
  };

  const ShowConfirmationPopup = (data: any): void => {
    setDialogInfo({
      dialogTitle: `${t('PRIVATE.PURGE.DELETE_DIALOG.TITLE', {ns: 'private'})}`,
      dialogDescription:
        `${t('PRIVATE.PURGE.DELETE_DIALOG.DESCRIPTION', {ns: 'private'})}` +
        ' ' +
        '?',
      yesLabelText: `${t('PRIVATE.ROLES.DELETE_DIALOG.YES_LABEL_TEXT', {
        ns: 'private'
      })}`,
      yesButtonColor: APP.PALETTE.ROLES.RED,
      noLabelText: `${t('PRIVATE.ROLES.DELETE_DIALOG.NO_LABEL_TEXT', {
        ns: 'private'
      })}`,
      noButtonColor: APP.PALETTE.ROLES.GRAY
    });

    setState(prevState => ({
      ...prevState,
      openConfirmationBox: true,
      rowId: data?.row?.ID
    }));
  };

  const dialogClose = () => {
    setState(prevState => ({
      ...prevState,
      openConfirmationBox: false
    }));
  };

  const renderDialog = (dialogProps: any): React.ReactElement => {
    return <ActionModalDialog {...dialogProps} />;
  };

  return (
    <div>
      {selectedAddNewTab ? (
        <Stack spacing={3} px={3}>
          <CustomBreadcrumbs
            heading={
              selectedAddNewTab === FormConstants.PURGE_TERMS.PURGE
                ? 'Purge'
                : 'Legal Hold'
            }
            links={[]}
            sx={{
              mt: 2
            }}
          />
          <ModuleSearchFilter
            uiFieldsData={
              selectedAddNewTab === FormConstants.PURGE_TERMS.PURGE
                ? tabData?.sub_sections[0]?.add_new?.searchForm
                : selectedAddNewTab === FormConstants.PURGE_TERMS.LEGAL_HOLD
                ? tabData?.sub_sections[1]?.add_new?.searchForm
                : ''
            }
            handleButtonSubmit={() => handleButtonSubmit()}
            handleCancelButton={() => handleCancelButton(selectedAddNewTab)}
          />
        </Stack>
      ) : !loader && tabData && Object.keys(tabData).length ? (
        <TabsComponent
          sectionUIData={tabData}
          sectionRowsData={[]}
          module={props?.module}
          handleAddNewRecord={handleAddNewRecord}
          handleDeleteLegalHold={ShowConfirmationPopup}
          apiLoader={apiLoader}
        />
      ) : (
        <Stack
          display={'flex'}
          justifyContent={'center'}
          alignItems={'center'}
          minHeight={'80vh'}>
          <LoadingScreen />
        </Stack>
      )}

      {openConfirmationBox &&
        renderDialog({
          open: openConfirmationBox,
          onClose: dialogClose,
          title: dialogInfo?.dialogTitle,
          description: dialogInfo?.dialogDescription,
          yesLabelText: dialogInfo?.yesLabelText,
          noLabelText: dialogInfo?.noLabelText,
          yesButtonColor: dialogInfo?.yesButtonColor,
          noButtonColor: dialogInfo?.noButtonColor,
          handleAgreeBtnClick: deleteLegalHold,
          handleCloseBtnClick: dialogClose,
          loader: dialogButtonLoader
        })}
    </div>
  );
};

export default PurgeAndLegalHoldComponent;
